import { enviroment } from "../../enviroment";

// Flag REACT_APP_LOGO_IMAGE_VERSION_AFTER_SAVE_ENABLED: the per-tab record of
// "this logo's stored images were rewritten at <ms>".
//
// The list and the popup show displayImage.lowResUrl with a ?v=<ms> token
// (mapLogo.appendVersion) that the API feeds from createdAtUtc and
// colorChange.startedAtUtc. A colour change in the editor is a chain of
// three mutations (ChangeLogoColor, ResizeImageLogo, UpdateImageLogo) that
// each overwrite the thumbnail blob under the SAME name, while startedAtUtc
// is set ONCE at the start of the chain: prod 19/10 21:29:56 token, blob
// rewritten 21:30:03 / 21:30:10 / 21:30:13, list refetched 21:30:27 with the
// new colours - and the browser kept the thumbnail it had cached under that
// token until a hard reload. The repository bumps this record when each of
// those answers lands; the mapper then takes max(API token, bump), so every
// row and popup mapped AFTER the last write carries a token the browser has
// not seen and fetches the finished blob. Zero extra API calls.
//
// Flag off: bump is a no-op and the read answers 0, so Math.max(apiVersion,
// 0) is exactly today's token (byte-identical). The record lives in the tab
// only: a new tab has no bump, and the API token is what it was before.

// A token strictly above the one already registered for the logo, so two
// answers inside the same millisecond still get two different tokens.
const MIN_TOKEN_STEP_MS = 1;

let versionsByLogoId: ReadonlyMap<number, number> = new Map();

function isValidLogoId(logoId: unknown): logoId is number {
  return typeof logoId === "number" && Number.isInteger(logoId) && logoId > 0;
}

function isValidClockMs(ms: unknown): ms is number {
  return typeof ms === "number" && Number.isFinite(ms) && ms > 0;
}

export function bumpLogoImageVersion(
  logoId: number,
  nowMs: number = Date.now(),
): void {
  if (!enviroment.logoImageVersionAfterSaveEnabled) return;
  if (!isValidLogoId(logoId) || !isValidClockMs(nowMs)) return;
  const current = versionsByLogoId.get(logoId);
  const next =
    current === undefined ? nowMs : Math.max(current + MIN_TOKEN_STEP_MS, nowMs);
  versionsByLogoId = withVersion(versionsByLogoId, logoId, next);
}

// A new map with one entry replaced - the registered map itself is never
// mutated (copied with forEach: the build target has no map spread).
function withVersion(
  versions: ReadonlyMap<number, number>,
  logoId: number,
  ms: number,
): ReadonlyMap<number, number> {
  const copy = new Map<number, number>();
  versions.forEach((value, key) => copy.set(key, value));
  copy.set(logoId, ms);
  return copy;
}

export function getLogoImageVersionBump(logoId: number): number {
  if (!enviroment.logoImageVersionAfterSaveEnabled) return 0;
  if (!isValidLogoId(logoId)) return 0;
  return versionsByLogoId.get(logoId) ?? 0;
}

// The fields of a logo answer that feed its image version. The dates come
// as ISO strings on the wire (LogoDto) and as Date on the mapped entity
// (BaseLogo, which is also how the API client types its raw answers).
type UtcStamp = string | Date | null | undefined;

export interface LogoImageAnswer {
  id?: unknown;
  createdAtUtc?: UtcStamp;
  colorChange?: { startedAtUtc?: UtcStamp } | null;
}

function parseUtcMs(value: UtcStamp): number {
  if (!value) return 0;
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) && ms > 0 ? ms : 0;
}

// The API's own image version for a logo: createdAtUtc carries LastWriteTime
// (never changed by a colour change) and colorChange.startedAtUtc is set once
// at the start of a colour change - the newer of the two is what the mapper
// puts in ?v= on its own. 0 when neither parses. Shared by the mapper and by
// the floor below, so the two can never disagree.
export function apiLogoImageVersion(
  answer: LogoImageAnswer | null | undefined,
): number {
  if (!answer) return 0;
  return Math.max(
    parseUtcMs(answer.createdAtUtc),
    parseUtcMs(answer.colorChange?.startedAtUtc),
  );
}

// The bump for a save answer whose stored images were just rewritten: the
// clock now, but never at or below the answer's own API token. The mapper
// takes max(API token, bump), so a client clock behind the API (by more than
// the save chain lasts) would otherwise register a bump that always loses -
// and the list would keep the cached thumbnail exactly as before the fix.
// With the floor, the token is above the API token whatever the clock says,
// and bumpLogoImageVersion keeps successive answers distinct and growing.
export function bumpLogoImageVersionForAnswer(
  answer: LogoImageAnswer | null | undefined,
  nowMs: number = Date.now(),
): void {
  if (!answer || !isValidLogoId(answer.id)) return;
  const floorMs = apiLogoImageVersion(answer) + MIN_TOKEN_STEP_MS;
  bumpLogoImageVersion(answer.id, Math.max(nowMs, floorMs));
}

export function resetLogoImageVersionsForTests(): void {
  versionsByLogoId = new Map();
}
